// ---------------------------------------------------------------------------
// Pure ISO date (yyyy-mm-dd string) arithmetic — zero dependency on
// DailySeries, AEMET, or any other domain concept. Shared by anything that
// needs to compute a date relative to another one: AEMET's windowed
// downloads (aemet.ts), ROCIO's own windowing (rocio.ts), and the
// calibration warm-up period (runCalibration.ts). Kept in its own module,
// separate even from dailySeries.ts, rather than folded into whichever
// domain module happened to need it first — this way nothing that only
// needs calendar math has to depend on the series/AEMET machinery to get
// it.
// ---------------------------------------------------------------------------

function isoFromUtcDate(d: Date): string {
  return `${String(d.getUTCFullYear()).padStart(4, "0")}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

export function addDaysIso(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  return isoFromUtcDate(new Date(Date.UTC(year, month - 1, day) + days * 86_400_000));
}

/**
 * Calendar month arithmetic (not a fixed ~30-day step) — e.g. subtracting a
 * month from the 31st of a month with fewer days than that rolls forward
 * into the following month, same as any `+N months` calendar operation
 * (JS Date's own day-overflow normalization). Used by runCalibration.ts's
 * warm-up period (days/weeks/months/years dropdown) — only needs to compute
 * an approximate lookback start date, not a value that round-trips exactly.
 */
export function addMonthsIso(isoDate: string, months: number): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  return isoFromUtcDate(new Date(Date.UTC(year, month - 1 + months, day)));
}

/** Same as `addMonthsIso`, in whole years. */
export function addYearsIso(isoDate: string, years: number): string {
  return addMonthsIso(isoDate, years * 12);
}

/** `b` minus `a`, in whole days — negative if `b` is earlier. Same use as `addDaysIso`. */
export function daysBetweenIso(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

/** 1-based day of year (Jan 1 = 1) — used by aemet.ts/rocio.ts's shared Hargreaves ETP formula. */
export function dayOfYear(isoDate: string): number {
  return daysBetweenIso(`${isoDate.slice(0, 4)}-01-01`, isoDate) + 1;
}

/** "yyyy-mm-dd" -> "dd/mm/yyyy", the date format every CSV export (results and project structure alike) writes, like the original .exe. */
export function isoToDdmmyyyy(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}
